import { Expression, Relation } from '@cotera/era';
import _ from 'lodash';
import { UserDefinedDimensions } from '../../cookbook';
import { ColSuccess, Identity } from './types';

const RECURSE_MAX_DEPTH = 1000;

export const applyRelColsInOrder = (opts: {
  relIdent: Identity;
  base: Relation;
  cols: Record<string, ColSuccess>;
}): Relation => {
  const { base, cols, relIdent } = opts;
  let curr: Relation = base;
  let remaining = Object.entries(cols);

  let loopCount = 0;
  while (remaining.length > 0) {
    loopCount++;

    const [level, pending] = _.partition(remaining, ([_name, x]) =>
      Object.keys(x.deps).every((dep) => Object.hasOwn(curr.attributes, dep))
    );

    remaining = pending;

    if (loopCount > RECURSE_MAX_DEPTH) {
      const msg = JSON.stringify(
        {
          attributes: Object.keys(curr.attributes),
          pending: renderDeps(pending),
        },
        null,
        2
      );

      throw new Error(`Logic error causing infininte loop, ${msg}`);
    }

    const udds = level
      .map(([name, { apply }]) =>
        apply.t === 'udd' ? ([name, apply] as const) : null
      )
      .filter((x) => x !== null);

    if (udds.length > 0) {
      const groupedUdds = _.groupBy(udds, ([_name, { identifier }]) =>
        identifier.toEraQL()
      );

      for (const [ident, group] of Object.entries(groupedUdds)) {
        const attributes = group.map(([name, { ty, key }]) => [
          name,
          { type: ty, key },
        ]);

        const udd = new UserDefinedDimensions({
          entityName: relIdent.toString(),
          attributes: Object.fromEntries(attributes),
        });

        curr = udd.join(curr, { identifier: (t) => t.eraql([ident]) });
      }
    }

    const newCols = level
      .map(([name, { apply }]): [string, Expression] | null =>
        apply.t === 'expr' ? ([name, apply.expr] as const) : null
      )
      .filter((x) => x !== null);

    curr = curr.select((t) => ({
      ...t.star(),
      ...Object.fromEntries(newCols),
    }));
  }
  return curr;
};

const renderDeps = (cols: [name: string, deps: ColSuccess][]) =>
  cols.map(([name, { deps }]) => [name, Object.keys(deps)]);
